import { SMSMessageDto } from '@act/shared/data-transfer-objects';
import { Button, FormBuilder } from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  CardActions,
  CardContent,
  CircularProgress,
  Dialog,
  Divider,
  Typography,
} from '@mui/material';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useToast } from 'react-components/app/ReusableToast';
import { SelectOption } from 'react-components/components/search-bar';
import { TrackingProvider } from 'react-components/tracking';
import { useForm } from 'react-hook-form';
import { callsRequests } from 'redux/requests/calls.request';
import { membersRequests } from 'redux/requests/members.request';
import { updateSubjectId } from 'redux/slices/callsSlice';
import { createPhoneNumber } from 'redux/slices/memberSlice';
import { assignSMSMessage } from 'redux/slices/smsMessagesSlice';
import { useAppDispatch } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';

import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import StyledAlert from 'toolkit/alerts/styled-alert';
import { GenericDialogProps } from 'utilities/dialog-context/dialog-context-helpers';
import { z } from 'zod';
import {
  DialogContentContainer,
  DialogTitleBarComponent,
} from '../dialog-forms/components/SharedDialogHookComponents';
import useUnsavedChangesDialog from '../dialog-forms/hooks/use-unsaved-changes-dialog';
import Loader from '../workflows/loader';

export interface AssignPhoneNumberUniqueProps {
  phoneNumber: string;
  callId?: number;
  sms?: SMSMessageDto;
}

export type AssignPhoneNumberDialogProps = GenericDialogProps<boolean> &
  AssignPhoneNumberUniqueProps;

export type AssignPhoneNumberDialogCallbackInput = {};

// Zod Schema
const AssignPhoneNumberSchema = z.object({
  interactableId: z
    .object({
      label: z.string(),
      value: z.string(),
    })
    .nullable()
    .refine(val => val !== null, {
      message: 'Please select a member',
    }),
  type: z.string().min(1, 'Phone type is required'),
  carrier: z.string(),
  description: z
    .string()
    .max(20, 'Description must be less than 20 characters')
    .optional(),
  number: z.string().min(1, 'Phone number is required'),
});

export type AssignPhoneNumberForm = z.infer<typeof AssignPhoneNumberSchema>;

const AssignPhoneNumberDialog = ({
  phoneNumber,
  callId,
  sms,
  open,
  setOpen,
}: AssignPhoneNumberDialogProps) => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();

  const { control, getValues, reset, formState, handleSubmit, setValue } =
    useForm<AssignPhoneNumberForm>({
      mode: 'onChange',
      resolver: zodResolver(AssignPhoneNumberSchema),
      defaultValues: {
        interactableId: null,
        type: '',
        carrier: '',
        description: '',
        number: '',
      },
    });

  const { isValid, isDirty } = formState;

  function handleClose() {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        reset();
        setOpen(false);
      });
    } else {
      reset();
      setOpen(false);
    }
  }

  const [phoneDetailsResponse, phoneDetailsLoading, getPhoneDetails] =
    useApiCall(callsRequests.getPhoneDetailsFromTwilio);

  const [typeaheadUsers, isLoadingTypeahead, getTypeaheadUsers] = useApiCall(
    membersRequests.getPaginatedMembersForTable
  );

  const [isAssigning, setIsAssigning] = useState(false);

  const [typeaheadOptions, setTypeaheadOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    if (typeaheadUsers?.users) {
      setTypeaheadOptions(
        typeaheadUsers.users.map(u => {
          return {
            label: `${u.firstName} ${u.lastName}`,
            value: u.id,
          };
        })
      );
    } else {
      setTypeaheadOptions([]);
    }
  }, [typeaheadUsers]);

  async function setTypeaheadSearchTerm(searchTerm: string) {
    const overrideFilters = {
      searchTerm,
      'page-start': 0,
      'page-size': 10,
      includeCount: true,
      orderBy: null,
      orderDirection: null,
      includeDetails: true,
    };
    const filterConfig = {
      ...overrideFilters,
    };
    void getTypeaheadUsers({ params: filterConfig });
  }

  useEffect(() => {
    if (phoneNumber) {
      // Extract only digits from the phone number
      const digitsOnly = phoneNumber.replace(/\D/g, '');
      // Remove leading 1 if present (US country code)
      const withoutCountryCode =
        digitsOnly.startsWith('1') && digitsOnly.length === 11
          ? digitsOnly.slice(1)
          : digitsOnly;
      // Take only the last 10 digits to ensure we have a valid US phone number
      const formattedNumber = withoutCountryCode.slice(-10);
      reset({
        ...getValues(),
        number: formattedNumber,
      });
      getPhoneDetails({ params: { phoneNumber } });
    }
  }, [phoneNumber]);

  useEffect(() => {
    reset({
      ...getValues(),
      type: phoneDetailsResponse?.type
        ? `${phoneDetailsResponse.type
            .slice(0, 1)
            .toLocaleUpperCase()}${phoneDetailsResponse.type.slice(1)}`
        : 'Unknown',
      carrier: phoneDetailsResponse?.carrier || 'not found',
    });
  }, [phoneDetailsResponse]);

  const isSubmitting = isAssigning || formState.isSubmitting;

  const formConfig = useMemo(
    () =>
      [
        {
          type: 'input',
          name: 'interactableId',
          label: 'Search by member name',
          input: 'searchBar',
          trackingName: 'member-search-input',
          props: {
            options: typeaheadOptions,
            isLoading: isLoadingTypeahead,
            onTypeaheadSearch: setTypeaheadSearchTerm,
            fullWidth: true,
            trackingName: 'member-search-input',
            onSearch: (searchTerm: string | SelectOption | null) => {
              if (searchTerm && typeof searchTerm === 'object') {
                // Use the actual SelectOption object
                setValue('interactableId', searchTerm, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              } else {
                // If it's a string or null, set to null
                setValue('interactableId', null, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }
            },
          },
        },
        {
          type: 'group',
          items: [
            {
              type: 'input',
              name: 'type',
              label: 'Phone type',
              input: 'text',
              trackingName: 'phone-type-input',
              props: {
                disabled: true,
                fullWidth: true,
              },
            },
            {
              type: 'input',
              name: 'description',
              label: 'Optional label',
              input: 'text',
              trackingName: 'phone-description-input',
              props: {
                fullWidth: true,
                characterLimit: 20,
              },
            },
          ],
        },
        {
          type: 'input',
          name: 'number',
          label: 'Phone Number',
          input: 'phone',
          trackingName: 'phone-number-input',
          disabled: true,
        },
      ] as FormConfig<AssignPhoneNumberForm>,
    [typeaheadOptions, isLoadingTypeahead, setTypeaheadSearchTerm, setValue]
  );

  async function onSubmit(data: AssignPhoneNumberForm) {
    try {
      const memberId = data.interactableId?.value;
      if (!memberId) {
        toast('Please select a member', 'error');
        return;
      }

      setIsAssigning(true);

      await dispatch(
        createPhoneNumber({
          params: { userId: memberId },
          body: {
            carrier: data.carrier,
            description: data.description || '',
            number: '+1'.concat(data.number.replace(/\D/g, '').slice(-10)),
            primary: false,
            type: data.type.toLocaleLowerCase(),
          },
        })
      );

      if (sms) {
        await dispatch(
          assignSMSMessage({
            body: { subjectId: memberId },
            params: { id: sms?.id },
          })
        );
      } else if (callId) {
        await dispatch(
          updateSubjectId({
            params: { id: callId },
            body: {
              subjectId: memberId,
            },
          })
        );
      }

      toast('Phone number assigned successfully!', 'success');
      reset();
      setOpen(false);
    } catch (error) {
      toast('An error occurred. Please try again.', 'error');
    } finally {
      setIsAssigning(false);
    }
  }

  return (
    <TrackingProvider contextName="assign-phone-number-dialog">
      <div style={{ width: '600px' }}>
        <Dialog open={open} onClose={handleClose} fullWidth>
          <DialogContentContainer>
            <DialogTitleBarComponent
              title="Assign phone number"
              handleClose={handleClose}
            />
            <CardContent sx={{ padding: '12px 24px 0' }}>
              {phoneDetailsLoading && <Loader />}
              {!phoneDetailsLoading && (
                <Fragment>
                  <StyledAlert
                    message="This number will be added to the member’s profile. This call and all future communication from this number will appear in the member’s feed."
                    severity="warning"
                    variant="tonal"
                  />
                  <br />
                  <Typography variant="titleMedium">
                    Select a member for this number
                  </Typography>
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <Box sx={{ margin: '8px 0 16px' }}>
                      <FormBuilder
                        config={formConfig}
                        control={control}
                        formState={formState}
                        disabled={isSubmitting}
                      />
                    </Box>
                  </form>
                </Fragment>
              )}
            </CardContent>
            <Divider sx={{ marginTop: '48px' }} />
            <CardActions
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'right',
                padding: '8px 24px',
              }}
            >
              <Button
                onClick={handleClose}
                disabled={isSubmitting}
                trackingName="cancel-button"
              >
                Cancel
              </Button>
              <Button
                disabled={!isValid || isSubmitting}
                onClick={handleSubmit(onSubmit)}
                type="submit"
                variant="contained"
                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                trackingName="assign-number-button"
              >
                {isSubmitting ? 'Assigning...' : 'Assign number'}
              </Button>
            </CardActions>
          </DialogContentContainer>
        </Dialog>
      </div>
    </TrackingProvider>
  );
};

export default AssignPhoneNumberDialog;
